import axios, {AxiosError, AxiosRequestConfig} from 'axios';
import {differenceInMinutes} from 'date-fns';
import i18next from 'i18next';

import {fetchAccessToken} from 'api/auth';
import {cleanAuthData} from 'models/auth';
import {TokensData} from 'types';

import {createRestError} from './errors';
import {Locales} from './localization';
import {TokenStorage} from './tokenStorage';

export const UNAUTHORIZED_ROUTES = ['/uaa/login', '/uaa/refresh-token'];

export const AUTH_TOKEN_NAME = 'Authorization';
export const CUSTOMER_LANGUAGE_HEADER = 'Customer-Language';
export const CUSTOMER_TIMEZONE_HEADER = 'Customer-Timezone';
export const CUSTOMER_LANGUAGE_MAP: Partial<Record<Locales, string>> = {
    [Locales.en]: 'en',
    [Locales.fr]: 'fr',
    [Locales.bs]: 'bs',
};
export const CUSTOMER_TIMEZONE = -new Date().getTimezoneOffset() / 60;
let REFRESH_TOKEN_PROMISE: null | Promise<TokensData> = null;

export const apiClient = axios.create({baseURL: '/api/v1'});

async function updateTokens(): Promise<TokensData | null> {
    const {refreshToken} = TokenStorage;
    if (!refreshToken) {
        REFRESH_TOKEN_PROMISE = null;
        return null;
    }

    try {
        if (!REFRESH_TOKEN_PROMISE) {
            TokenStorage.accessToken = null;
            REFRESH_TOKEN_PROMISE = fetchAccessToken(refreshToken);
        }

        const tokensData = await REFRESH_TOKEN_PROMISE;
        TokenStorage.storeTokens(tokensData);
        REFRESH_TOKEN_PROMISE = null;
        return tokensData;
    } catch (refreshTokenError) {
        REFRESH_TOKEN_PROMISE = null;
        return null;
    }
}

export async function updateTokensBeforeExpire(minutes = 1) {
    const date = new Date(TokenStorage.accessTokenExpiresIn?.toString() ?? new Date());
    if (differenceInMinutes(date, new Date()) < minutes) await updateTokens();
}

async function onErrorInterceptor(e: AxiosError) {
    if (e.response?.status !== 401) throw createRestError(e);

    const tokensData = await updateTokens();
    if (!tokensData) {
        cleanAuthData();
        throw new Error(i18next.t('errors.auth.invalidRefreshToken'));
    }

    e.config.headers[AUTH_TOKEN_NAME] = `Bearer ${tokensData.accessToken}`;
    return apiClient.request(e.config);
}

async function onRequestInterceptor(clientConfig: AxiosRequestConfig) {
    const {url} = clientConfig;

    clientConfig.headers[CUSTOMER_LANGUAGE_HEADER] =
        CUSTOMER_LANGUAGE_MAP[i18next.language as Locales] ?? CUSTOMER_LANGUAGE_MAP[Locales.en];
    clientConfig.headers[CUSTOMER_TIMEZONE_HEADER] =
        clientConfig.headers[CUSTOMER_TIMEZONE_HEADER] ?? CUSTOMER_TIMEZONE;

    if (url != null && UNAUTHORIZED_ROUTES.includes(url)) {
        clientConfig.headers[AUTH_TOKEN_NAME] = undefined;
        return clientConfig;
    }

    const {accessToken, accessTokenExpiresIn} = TokenStorage;

    if (
        accessToken == null ||
        accessTokenExpiresIn == null ||
        new Date() >= new Date(accessTokenExpiresIn)
    ) {
        const tokensData = await updateTokens();
        if (tokensData != null) {
            clientConfig.headers[AUTH_TOKEN_NAME] = `Bearer ${tokensData.accessToken}`;
            return clientConfig;
        }
    }

    clientConfig.headers[AUTH_TOKEN_NAME] = accessToken ? `Bearer ${accessToken}` : undefined;
    return clientConfig;
}

apiClient.interceptors.response.use((response) => response, onErrorInterceptor);
apiClient.interceptors.request.use(onRequestInterceptor);
